// ---------------------------------------------------------------------------
// Section 6: Typography — font size, weight, text color, line height
// ---------------------------------------------------------------------------

import { InspectorSection } from '@/components/inspector'
import {
  InlineSelectField,
  InlineColorField,
  FONT_SIZE_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  LINE_HEIGHT_OPTIONS,
} from '../fields'
import {
  extractFontSize,
  setFontSize,
  extractFontWeight,
  setFontWeight,
  extractTextColor,
  setTextColor,
  extractLeading,
  setLeading,
} from '@/features/page-editor/lib/class-utils'
import type { CogoElement } from '@/features/page-editor/store/types'

export function TypographySection({
  element,
  updateClass,
}: {
  element: CogoElement
  updateClass: (transform: (cls: string) => string) => void
}) {
  const cls = element.className ?? ''

  // Extract from className first, fall back to direct element properties (Coogo JSON schema)
  const fontSize = extractFontSize(cls) || (element.size ?? '')
  const fontWeight = extractFontWeight(cls) || (element.weight ?? '')
  const textColor = extractTextColor(cls) || (element.color ?? '')
  const leading = extractLeading(cls)

  // Auto-collapse if no typography values are set (check both className and element props)
  const hasValues = !!(fontSize || fontWeight || textColor || leading)

  return (
    <InspectorSection title="Typography" defaultOpen={hasValues}>
      {/* Font Size */}
      <InlineSelectField
        label="Size"
        value={fontSize}
        onChange={(val) => updateClass((c) => setFontSize(c, val))}
        options={FONT_SIZE_OPTIONS}
        show
      />

      {/* Font Weight */}
      <InlineSelectField
        label="Weight"
        value={fontWeight}
        onChange={(val) => updateClass((c) => setFontWeight(c, val))}
        options={FONT_WEIGHT_OPTIONS}
        show
      />

      {/* Text Color */}
      <InlineColorField
        label="Color"
        value={textColor}
        onChange={(color) => updateClass((c) => setTextColor(c, color))}
      />

      {/* Line Height */}
      <InlineSelectField
        label="Leading"
        value={leading}
        onChange={(val) => updateClass((c) => setLeading(c, val))}
        options={LINE_HEIGHT_OPTIONS}
        show
      />
    </InspectorSection>
  )
}
